import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Icon,
  LoadingIcon,
  NewButton,
  P,
  VStack,
} from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

type DownloadStatus = 'idle' | 'loading' | 'success' | 'error';

const DownloadingDialog: React.FC<{
  status: DownloadStatus;
  isOpen: boolean;
  onClose: () => void;
}> = ({ status, isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Downloading Medical Codes</DialogTitle>
        </DialogHeader>
        <VStack justify="center" align="center" className="py-6">
          {status === 'loading' && (
            <>
              <LoadingIcon
                size="lg"
                className="mb-4 text-text-accent-secondary"
              />
              <P>Preparing your download...</P>
            </>
          )}
          {status === 'success' && (
            <>
              <Icon
                name="CheckCircle2"
                size="lg"
                className="mb-4 text-text-positive"
              />
              <P>Download complete!</P>
            </>
          )}
          {status === 'error' && (
            <>
              <Icon
                name="AlertCircle"
                size="lg"
                className="mb-4 text-text-negative"
              />
              <P>Failed to download medical codes. Please try again.</P>
            </>
          )}
        </VStack>
      </DialogContent>
    </Dialog>
  );
};

export const DownloadCodesButton: React.FC<{
  hasEnabledCodes: boolean;
}> = ({ hasEnabledCodes }) => {
  const tenantId = useAssignedTenantIdFromParams();
  const { userGroupId } = useParams() as { userGroupId: string };
  const logger = useNamedLogger('UserGroupCodeConfigs');

  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>('idle');
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);

  const apiClient = useApiClient();

  const downloadCodes = useCallback(
    async (downloadType: 'all' | 'group') => {
      setDownloadStatus('loading');
      setShowDownloadDialog(true);

      let response;
      let filename;
      let errorMessage;

      try {
        if (downloadType === 'all') {
          response =
            await apiClient.medicalCodes.generateDownloadUrlForAllCodes({
              params: { tenantId },
            });
          filename = 'all-available-medical-codes.csv';
          errorMessage = 'Error downloading medical codes:';
        } else {
          // Request a download URL for group-enabled codes
          response =
            await apiClient.medicalCodes.generateDownloadUrlForGroupEnabledCodes(
              {
                params: { tenantId, userGroupId },
                body: {},
              },
            );
          filename = 'group-enabled-medical-codes.csv';
          errorMessage = 'Error downloading group-enabled medical codes:';
        }

        if (response.status !== 200) {
          throw new Error('Failed to generate download URL');
        }

        const { downloadUrl } = response.body;

        // Create a temporary link to trigger the download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setDownloadStatus('success');

        // Automatically close the dialog after success (3 seconds)
        setTimeout(() => {
          if (downloadStatus === 'success') {
            setShowDownloadDialog(false);
            setDownloadStatus('idle');
          }
        }, 3000);
      } catch (error) {
        logger.error(errorMessage || 'Error downloading medical codes:', {
          error,
        });
        setDownloadStatus('error');
      }
    },
    [apiClient, tenantId, userGroupId, downloadStatus, logger],
  );

  const handleCloseDownloadDialog = useCallback(() => {
    setShowDownloadDialog(false);
    if (downloadStatus === 'error') {
      setDownloadStatus('idle');
    }
  }, [downloadStatus]);

  return (
    <>
      <NewButton
        text="Download All Available Codes"
        onClick={() => downloadCodes('all')}
      />
      <NewButton
        text="Download Enabled Codes"
        onClick={() => downloadCodes('group')}
        disabled={!hasEnabledCodes}
      />

      {/* Download Status Dialog */}
      <DownloadingDialog
        status={downloadStatus}
        isOpen={showDownloadDialog}
        onClose={handleCloseDownloadDialog}
      />
    </>
  );
};
